/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Box,
    Center,
    useMultiStyleConfig,
    Tooltip
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'
import {tooltipStyle} from '../product-view/index.style'

/**
 * The Swatch Component displays item inside `SwatchGroup`. For proper keyboard accessibility,
 * ensure that the rendered elements can receive keyboard focus, and are immediate siblings.
 */
const Swatch = ({
    children,
    disabled,
    href,
    label,
    name,
    variantName,
    selected,
    isFocusable,
    value,
    handleSelect,
    variant = 'square'
}) => {
    const styles = useMultiStyleConfig('SwatchGroup', {variant, disabled, selected})
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [selectHandlers, setSelectHandlers] = useState({})

    const onSelect = useCallback(
        (e) => {
            e.preventDefault()
            handleSelect(value)
        },
        [handleSelect]
    )

    useEffect(() => {
        if (!handleSelect) {
            return
        }

        setSelectHandlers({
            [isDesktop ? 'onMouseEnter' : 'onClick']: onSelect
        })
    }, [onSelect, isDesktop])

    return (
        <Tooltip
            label={disabled ? `${variantName} ${value} is not available for this combination` : ''}
            sx={tooltipStyle}
        >
            <Button
                {...styles.swatch}
                as={href && !disabled ? RouteLink : 'button'}
                to={href && !disabled ? href : undefined}
                aria-label={name}
                aria-checked={selected}
                variant="outline"
                role="radio"
                minWidth={[pxToRem(40), pxToRem(40), pxToRem(50), pxToRem(50)]}
                height={[pxToRem(25.6), pxToRem(25.6), pxToRem(32), pxToRem(32)]}
                marginRight={[pxToRem(5), pxToRem(5), pxToRem(10), pxToRem(10)]}
                marginBottom={[pxToRem(5), pxToRem(5), pxToRem(10), pxToRem(10)]}
                paddingLeft={pxToRem(5)}
                paddingRight={pxToRem(5)}
                borderRadius={0}
                fontFamily={fonts.primary}
                fontSize={[`${pxToRem(10)}`, `${pxToRem(10)}`, `${pxToRem(12)}`, `${pxToRem(12)}`]}
                fontStyle={'normal'}
                fontWeight={400}
                lineHeight={'normal'}
                letterSpacing={`${pxToRem(1)}`}
                textTransform={'uppercase'}
                tabIndex={isFocusable ? 0 : -1}
                {...(href && !disabled ? {} : selectHandlers)}
            >
                <Center {...styles.swatchButton}>
                    {children}
                    {label && <Box>{label}</Box>}
                </Center>
            </Button>
        </Tooltip>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * Whether the swatch can receive tab focus
     */
    isFocusable: PropTypes.bool,
    /**
     * This function is called whenever the mouse enters the swatch on desktop or when clicked on mobile.
     * The values is passed as the first argument.
     */
    handleSelect: PropTypes.func,
    /**
     * The variant name of the swatch, used for accessibility.
     */
    variantName: PropTypes.string
}

export default Swatch
